import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  DatePicker,
  Modal,
  notification,
  Tooltip,
  TreeSelect,
} from 'antd'
import moment from 'moment'
import styled from 'styled-components'
import { saveAs } from 'file-saver'
import { expressAPI } from 'src/utils/interceptor'
import ReportGroupSelect from './ReportGroupSelect'

export const GP_STOCK_PERFORMANCE_REPORT_NAME = 'GP & Stock Performance Report'
export const GP_STOCK_REPORT_PREFS_KEY = 'oms.gpStockPerformanceReport.prefs'
export const HOTEL_NAME_MAX_LENGTH = 120
export const TARGET_NOT_CONFIGURED = 'Target not configured'
export const TARGET_COS_PERCENT_MAX = 100
export const TARGET_COS_PERCENT_PLACEHOLDER = 'e.g. 30'
/** Kitchen COS is usually 25–30. Values above this look like a GP target. */
export const TARGET_COS_LOOKS_LIKE_GP_PERCENT = 50
export const LONG_RANGE_DAYS = 90
export const HEAVY_VENUE_COUNT = 20
export const REPORT_VENUE_CONFIRM_COUNT = 40
export const GP_STOCK_REPORT_TIMEOUT_MS = 2 * 60 * 1000
export const GP_STOCK_REPORT_TIMEOUT_MESSAGE =
  'The report took too long to generate. Try fewer venues or a shorter period.'
export const GP_STOCK_REPORT_GENERIC_ERROR =
  'We could not generate the report. Try fewer venues or a shorter period.'
export const GP_STOCK_REPORT_EXCEL_CONTENT_TYPE =
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
export const GP_STOCK_REPORT_ROUTE = 'venues/gp-stock-performance-report'
/** Open venue list stays in this box so it cannot cover Target COS / Run below. */
export const GP_VENUE_DROPDOWN_MAX_HEIGHT_PX = 180
export const GP_VENUE_SELECT_MIN_HEIGHT_PX = 40

/** Shown under the report-type dropdown. Keep in step with Risk Tables intro. */
export const GP_STOCK_REPORT_HELPER_POINTS = Object.freeze([
  'once per hotel',
  'do not mix hotels',
  'own tab',
  'delete unused tabs',
  'ex VAT',
  'entered ex VAT',
  'Missing main stocktakes are flagged',
])

const dateFormat = 'DD/MM/YYYY'
const monthFormat = 'MMMM YYYY'

export function getDefaultMonthMoment(now = moment()) {
  return now.clone().subtract(1, 'month').startOf('month')
}

export function monthToDateRange(monthMoment) {
  if (!monthMoment || !monthMoment.isValid?.()) return [null, null]
  return [
    monthMoment.clone().startOf('month'),
    monthMoment.clone().endOf('month'),
  ]
}

export function getInclusiveRangeDays(start, end) {
  if (!start || !end || !start.isValid?.() || !end.isValid?.()) return 0
  return (
    end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1
  )
}

export function shouldWarnLongRange(dayCount) {
  return dayCount > LONG_RANGE_DAYS
}

export function shouldWarnHeavyReport(venueCount) {
  return Number(venueCount) >= HEAVY_VENUE_COUNT
}

export function shouldConfirmGpVenueCount(count) {
  return Number(count) > REPORT_VENUE_CONFIRM_COUNT
}

export function gpLargeGroupQuestion(count) {
  return `This GP report has ${count} venues. Generate it now?`
}

export const gpGroupMissingLine = ({ hotelName } = {}) =>
  String(hotelName ?? '').trim()
    ? ''
    : 'The hotel name is still required before Run.'

export function targetCosLooksLikeGpPercent(value) {
  const amount = Number(value)
  return Number.isFinite(amount) && amount > TARGET_COS_LOOKS_LIKE_GP_PERCENT
}

export function getGpShapedTargetWarnings(
  venues,
  targetsByVenue,
  selectedVenueIds,
) {
  const source =
    targetsByVenue && typeof targetsByVenue === 'object' ? targetsByVenue : {}
  const byId = new Map((venues || []).map(venue => [String(venue._id), venue]))
  const warnings = []
  for (const id of selectedVenueIds || []) {
    const raw = source[id] ?? source[String(id)]
    if (!targetCosLooksLikeGpPercent(raw)) continue
    const venue = byId.get(String(id))
    warnings.push({
      id: String(id),
      title: venue?.title || 'Venue',
      percent: Number(raw),
    })
  }
  return warnings
}

export function formatGpShapedTargetWarning(warnings = []) {
  if (!warnings.length) return ''
  const list = warnings.map(item => `${item.title} ${item.percent}%`).join(', ')
  const verb = warnings.length === 1 ? 'looks' : 'look'
  return `${list} ${verb} like GP %, not kitchen COS (often 25–30). You can still run the report.`
}

export function getFilenameFromContentDisposition(headerValue) {
  if (!headerValue) return ''
  const utf8Match = headerValue.match(/filename\*=UTF-8''([^;]+)/i)
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1]).replace(/["']/g, '')
  }

  const fallbackMatch = headerValue.match(/filename="?([^"]+)"?/i)
  return fallbackMatch?.[1] || ''
}

export function formatGpStockPeriodLabel(start, end) {
  const startDate = moment(start, 'YYYY-MM-DD', true)
  const endDate = moment(end, 'YYYY-MM-DD', true)
  if (!startDate.isValid() || !endDate.isValid()) return 'Period'

  const isCalendarMonth =
    startDate.isSame(startDate.clone().startOf('month'), 'day') &&
    endDate.isSame(endDate.clone().endOf('month'), 'day') &&
    startDate.isSame(endDate, 'month') &&
    startDate.isSame(endDate, 'year')

  if (isCalendarMonth) return startDate.format('MMMM YYYY')
  return `${startDate.format('D MMM YYYY')} to ${endDate.format('D MMM YYYY')}`
}

export function buildGpStockFallbackFilename({ hotelName, start, end } = {}) {
  const hotel =
    String(hotelName ?? '')
      .replace(/[\\/:*?"<>|]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim() || 'Hotel'
  const period = formatGpStockPeriodLabel(start, end)
  return `${GP_STOCK_PERFORMANCE_REPORT_NAME} - ${hotel} - ${period}.xlsx`
}

export function formatGpStockDownloadSuccess(filename) {
  return `Downloaded ${filename}`
}

export function buildTargetsPayload(targetsByVenue, selectedVenueIds) {
  const out = {}
  const source =
    targetsByVenue && typeof targetsByVenue === 'object' ? targetsByVenue : {}
  for (const id of selectedVenueIds || []) {
    const raw = source[id] ?? source[String(id)]
    if (raw == null || String(raw).trim() === '') continue
    const amount = Number(raw)
    if (!Number.isFinite(amount) || amount < 0) continue
    const capped = Math.min(amount, TARGET_COS_PERCENT_MAX)
    out[id] = capped
  }
  return out
}

export async function readGpStockReportErrorMessage(error) {
  if (
    error?.code === 'ECONNABORTED' ||
    /timeout/i.test(String(error?.message || ''))
  ) {
    return GP_STOCK_REPORT_TIMEOUT_MESSAGE
  }

  const status = error?.response?.status
  const data = error?.response?.data
  if (data && typeof data === 'object' && typeof data.message === 'string') {
    return data.message
  }

  const text = await readResponseBodyText(data)
  if (text) {
    try {
      const parsed = JSON.parse(text)
      if (parsed && typeof parsed === 'object' && parsed.message) {
        return String(parsed.message)
      }
    } catch {
      if (text.trim()) return text.trim()
    }
  }

  if (status === 504) return GP_STOCK_REPORT_TIMEOUT_MESSAGE
  return GP_STOCK_REPORT_GENERIC_ERROR
}

async function readResponseBodyText(data) {
  if (data == null) return ''
  if (typeof data === 'string') return data
  if (typeof data.text === 'function') {
    try {
      return await data.text()
    } catch {
      return ''
    }
  }
  if (typeof FileReader === 'undefined' || typeof data.size !== 'number') {
    return ''
  }
  return new Promise(resolve => {
    try {
      const reader = new FileReader()
      reader.onload = () => resolve(String(reader.result || ''))
      reader.onerror = () => resolve('')
      reader.readAsText(data)
    } catch {
      resolve('')
    }
  })
}

function getStorage(storage) {
  if (storage) return storage
  if (typeof window === 'undefined' || !window.localStorage) return null
  return window.localStorage
}

export function normalizeGpStockReportPrefs(raw = {}) {
  const hotelName = String(raw.hotelName ?? '')
    .trim()
    .slice(0, HOTEL_NAME_MAX_LENGTH)

  const selectedVenueIds = Array.isArray(raw.selectedVenueIds)
    ? [...new Set(raw.selectedVenueIds.map(id => String(id)).filter(Boolean))]
    : []

  const targetsByVenue = {}
  const rawTargets = raw.targetsByVenue
  if (
    rawTargets &&
    typeof rawTargets === 'object' &&
    !Array.isArray(rawTargets)
  ) {
    for (const [id, value] of Object.entries(rawTargets)) {
      const text = String(value ?? '').trim()
      if (!id || text === '') continue
      const sanitized = sanitizeTargetCosPercent(text)
      if (sanitized) targetsByVenue[String(id)] = sanitized
    }
  }

  return { hotelName, selectedVenueIds, targetsByVenue }
}

export function loadGpStockReportPrefs(storage) {
  const store = getStorage(storage)
  if (!store) return normalizeGpStockReportPrefs()
  try {
    const raw = store.getItem(GP_STOCK_REPORT_PREFS_KEY)
    if (!raw) return normalizeGpStockReportPrefs()
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return normalizeGpStockReportPrefs()
    }
    return normalizeGpStockReportPrefs(parsed)
  } catch {
    return normalizeGpStockReportPrefs()
  }
}

export function saveGpStockReportPrefs(prefs, storage) {
  const store = getStorage(storage)
  if (!store) return false
  try {
    store.setItem(
      GP_STOCK_REPORT_PREFS_KEY,
      JSON.stringify(normalizeGpStockReportPrefs(prefs)),
    )
    return true
  } catch {
    return false
  }
}

function authorisedVenueIds(ids, venues) {
  const allowed = new Set((venues || []).map(item => String(item._id)))
  return (ids || []).filter(id => allowed.has(String(id)))
}

export function sanitizeTargetCosPercent(raw) {
  if (raw == null || raw === '') return ''
  const text = String(raw)
  if (text.trim() === '') return ''
  const trimmed = text.trim()
  if (/^\d{1,3}\.$/.test(trimmed)) {
    const whole = Number(trimmed.slice(0, -1))
    if (!Number.isFinite(whole) || whole < 0) return null
    if (whole > TARGET_COS_PERCENT_MAX) return String(TARGET_COS_PERCENT_MAX)
    return trimmed
  }
  const amount = Number(trimmed)
  if (!Number.isFinite(amount) || amount < 0) return null
  if (amount > TARGET_COS_PERCENT_MAX) return String(TARGET_COS_PERCENT_MAX)
  return trimmed
}

export function filterGpVenueTreeNode(input, node) {
  const raw = node?.title ?? node?.props?.title
  if (typeof raw !== 'string') return false
  return raw.toLowerCase().includes(
    String(input || '')
      .trim()
      .toLowerCase(),
  )
}

/**
 * Venues + period + hotel name + per-venue Target COS % + Excel download.
 */
const GpStockPerformanceReport = ({ venues = [] }) => {
  const [groupChosen, setGroupChosen] = useState(false)
  const [hotelName, setHotelName] = useState(
    () => loadGpStockReportPrefs().hotelName,
  )
  const [targetsByVenue, setTargetsByVenue] = useState(
    () => loadGpStockReportPrefs().targetsByVenue,
  )
  const [selectedVenueIds, setSelectedVenueIds] = useState(() =>
    authorisedVenueIds(loadGpStockReportPrefs().selectedVenueIds, venues),
  )
  const [dateMode, setDateMode] = useState('month')
  const [selectedMonth, setSelectedMonth] = useState(() =>
    getDefaultMonthMoment(),
  )
  const [customRange, setCustomRange] = useState(() =>
    monthToDateRange(getDefaultMonthMoment()),
  )
  const [loadingReport, setLoadingReport] = useState(false)
  const [downloadedFilename, setDownloadedFilename] = useState('')
  const [venueDropdownOpen, setVenueDropdownOpen] = useState(false)
  const venuePickerRef = useRef(null)

  const venueOptions = useMemo(
    () =>
      venues.map(item => ({
        title: item.title,
        value: item._id,
      })),
    [venues],
  )
  const allVenueIds = useMemo(
    () => venueOptions.map(({ value }) => value),
    [venueOptions],
  )
  const selectedVenues = useMemo(
    () =>
      selectedVenueIds
        .map(id => venues.find(item => String(item._id) === String(id)))
        .filter(Boolean),
    [selectedVenueIds, venues],
  )

  const effectiveRange = useMemo(() => {
    if (dateMode === 'month') {
      return monthToDateRange(selectedMonth)
    }
    return customRange || [null, null]
  }, [dateMode, selectedMonth, customRange])

  const rangeDayCount = getInclusiveRangeDays(
    effectiveRange?.[0],
    effectiveRange?.[1],
  )
  const showLongRangeWarning = shouldWarnLongRange(rangeDayCount)
  const showHeavyReportWarning = shouldWarnHeavyReport(
    selectedVenueIds.length,
    rangeDayCount,
  )
  const gpShapedTargetWarnings = getGpShapedTargetWarnings(
    venues,
    targetsByVenue,
    selectedVenueIds,
  )
  const gpShapedTargetWarningText = formatGpShapedTargetWarning(
    gpShapedTargetWarnings,
  )

  useEffect(() => {
    saveGpStockReportPrefs({
      hotelName,
      selectedVenueIds,
      targetsByVenue,
    })
  }, [hotelName, selectedVenueIds, targetsByVenue])

  const onTargetChange = (venueId, raw) => {
    const next = sanitizeTargetCosPercent(raw)
    if (next == null) return
    setTargetsByVenue(prev => {
      const updated = { ...prev }
      if (next === '') {
        delete updated[venueId]
        delete updated[String(venueId)]
      } else {
        updated[venueId] = next
      }
      return updated
    })
  }

  const canRunReport =
    selectedVenueIds.length > 0 &&
    Boolean(effectiveRange?.[0] && effectiveRange?.[1]) &&
    Boolean(hotelName.trim())

  const runReport = async () => {
    if (!canRunReport || loadingReport) return

    const start = effectiveRange[0].format('YYYY-MM-DD')
    const end = effectiveRange[1].format('YYYY-MM-DD')
    const trimmedHotel = hotelName.trim()

    setLoadingReport(true)
    setDownloadedFilename('')
    try {
      const response = await expressAPI.post(
        GP_STOCK_REPORT_ROUTE,
        {
          venueIds: selectedVenueIds,
          start,
          end,
          hotelName: trimmedHotel,
          targetsByVenue: buildTargetsPayload(targetsByVenue, selectedVenueIds),
        },
        {
          responseType: 'blob',
          timeout: GP_STOCK_REPORT_TIMEOUT_MS,
        },
      )

      const filename =
        getFilenameFromContentDisposition(
          response.headers?.['content-disposition'],
        ) ||
        buildGpStockFallbackFilename({
          hotelName: trimmedHotel,
          start,
          end,
        })

      const blob =
        response?.data instanceof Blob
          ? response.data
          : new Blob([response?.data || new Uint8Array()], {
              type: GP_STOCK_REPORT_EXCEL_CONTENT_TYPE,
            })
      saveAs(blob, filename)
      setDownloadedFilename(filename)

      notification.success({
        message: 'Report generated',
        description: 'GP & Stock Performance workbook downloaded.',
      })
    } catch (error) {
      console.error('Failed to download GP & Stock Performance report:', error)
      notification.error({
        message: 'Report failed',
        description: await readGpStockReportErrorMessage(error),
      })
    } finally {
      setLoadingReport(false)
    }
  }

  const askThenRun = () => {
    if (!shouldConfirmGpVenueCount(selectedVenueIds.length)) {
      runReport()
      return
    }
    Modal.confirm({
      title: 'Large GP report',
      content: gpLargeGroupQuestion(selectedVenueIds.length),
      okText: 'Generate report',
      cancelText: 'Cancel',
      onOk: () => runReport(),
    })
  }

  return (
    <StyledBlock
      data-testid='gp-stock-performance-report-panel'
      data-venue-count={venues.length}
    >
      <HelperText data-testid='gp-stock-performance-helper'>
        Run this <strong>once per hotel</strong>. Select only that hotel’s
        venues — do not mix hotels in the same file. Each selected venue is its
        own tab; delete unused tabs that do not belong to this run.
        <br />
        <br />
        All figures are <strong>ex VAT</strong>. Revenue used in Gross Profit
        must be entered ex VAT. Missing main stocktakes are flagged.
      </HelperText>

      <FieldLabel htmlFor='gp-hotel-name'>Hotel / property name</FieldLabel>
      <StyledTextInput
        id='gp-hotel-name'
        data-testid='hotel-name-input'
        type='text'
        maxLength={HOTEL_NAME_MAX_LENGTH}
        placeholder='e.g. The Grand'
        value={hotelName}
        onChange={e => setHotelName(e.target.value)}
      />

      <ReportGroupSelect
        venues={venues}
        onPick={ids => {
          const next = ids || []
          setSelectedVenueIds(next)
          setGroupChosen(next.length > 0)
        }}
      />
      {groupChosen && gpGroupMissingLine({ hotelName }) ? (
        <p data-testid='gp-group-still-required'>
          {gpGroupMissingLine({ hotelName })}
        </p>
      ) : null}
      <VenuePicker
        ref={venuePickerRef}
        data-testid='gp-venue-picker'
        data-dropdown-open={venueDropdownOpen ? 'true' : 'false'}
        data-dropdown-max-height={String(GP_VENUE_DROPDOWN_MAX_HEIGHT_PX)}
        style={
          venueDropdownOpen
            ? {
                minHeight:
                  GP_VENUE_SELECT_MIN_HEIGHT_PX +
                  GP_VENUE_DROPDOWN_MAX_HEIGHT_PX,
              }
            : undefined
        }
      >
        <TreeSelect
          showSearch
          treeNodeFilterProp='title'
          filterTreeNode={filterGpVenueTreeNode}
          allowClear
          placeholder='Select Venues ...'
          treeCheckable
          showCheckedStrategy={TreeSelect.SHOW_CHILD}
          dropdownStyle={{
            maxHeight: GP_VENUE_DROPDOWN_MAX_HEIGHT_PX,
            overflow: 'auto',
          }}
          style={{ width: '100%' }}
          getPopupContainer={() => venuePickerRef.current || document.body}
          onDropdownVisibleChange={open => setVenueDropdownOpen(Boolean(open))}
          onChange={ids => setSelectedVenueIds(ids)}
          value={selectedVenueIds}
          treeData={[
            {
              title:
                selectedVenueIds.length > 0 ? (
                  <span
                    onClick={() => setSelectedVenueIds([])}
                    style={{
                      display: 'inline-block',
                      color: '#b5d922',
                      cursor: 'pointer',
                    }}
                  >
                    Unselect all
                  </span>
                ) : (
                  <span
                    onClick={() => setSelectedVenueIds(allVenueIds)}
                    style={{
                      display: 'inline-block',
                      color: '#b5d922',
                      cursor: 'pointer',
                    }}
                  >
                    Select all
                  </span>
                ),
              value: 'select-all-venues',
              disableCheckbox: true,
              disabled: true,
            },
            ...venueOptions,
          ]}
        />
      </VenuePicker>

      <ModeRow>
        <ModeButton
          type='button'
          $active={dateMode === 'month'}
          onClick={() => setDateMode('month')}
          data-testid='date-mode-month'
        >
          Month
        </ModeButton>
        <ModeButton
          type='button'
          $active={dateMode === 'custom'}
          onClick={() => setDateMode('custom')}
          data-testid='date-mode-custom'
        >
          Custom range
        </ModeButton>
      </ModeRow>

      {dateMode === 'month' ? (
        <StyledMonthPicker
          data-testid='month-picker'
          value={selectedMonth}
          onChange={value => {
            setSelectedMonth(value)
            if (value) {
              setCustomRange(monthToDateRange(value))
            }
          }}
          format={monthFormat}
          allowClear={false}
          style={{ width: '355px', marginBottom: '15px' }}
          placeholder='Select month'
        />
      ) : (
        <StyledRangePicker
          data-testid='date-range-picker'
          value={customRange}
          onChange={value => setCustomRange(value)}
          format={dateFormat}
          style={{ width: '355px', marginBottom: '15px' }}
        />
      )}

      {showLongRangeWarning && (
        <WarningText data-testid='warning-long-range'>
          This range is longer than 90 days. Generation may take a while —
          please keep this window open until the download finishes. You can
          still run the report.
        </WarningText>
      )}

      {showHeavyReportWarning && (
        <WarningText data-testid='warning-heavy-report'>
          You selected {selectedVenueIds.length} venues. This can take a minute
          or more, including for a normal month. This is a warning only — it
          does not block the download.
        </WarningText>
      )}

      {selectedVenues.length > 0 && (
        <TargetsBlock data-testid='target-cos-fields'>
          <FieldLabel>Target COS %</FieldLabel>
          <FieldHint>
            Enter a percentage from 0 to {TARGET_COS_PERCENT_MAX} (kitchen cost,
            not GP % — often 25–30). Leave blank to show “
            {TARGET_NOT_CONFIGURED}” on that venue’s tab.
          </FieldHint>
          {selectedVenues.map(venue => (
            <TargetRow key={venue._id}>
              <TargetVenueName title={venue.title}>
                {venue.title}
              </TargetVenueName>
              <PercentField>
                <StyledTextInput
                  data-testid={`target-cos-${venue._id}`}
                  type='number'
                  min='0'
                  max={TARGET_COS_PERCENT_MAX}
                  step='0.01'
                  inputMode='decimal'
                  aria-label={`${venue.title} target COS percent`}
                  placeholder={TARGET_COS_PERCENT_PLACEHOLDER}
                  value={
                    targetsByVenue[venue._id] ??
                    targetsByVenue[String(venue._id)] ??
                    ''
                  }
                  onChange={e => onTargetChange(venue._id, e.target.value)}
                />
                <PercentSuffix aria-hidden='true'>%</PercentSuffix>
              </PercentField>
            </TargetRow>
          ))}
        </TargetsBlock>
      )}

      {gpShapedTargetWarningText ? (
        <WarningText data-testid='warning-target-looks-like-gp'>
          {gpShapedTargetWarningText}
        </WarningText>
      ) : null}

      {!canRunReport ? (
        <RunFooter
          data-testid='gp-run-report-footer'
          style={{ position: 'sticky', bottom: 0 }}
        >
          <Tooltip title='Select venues, a period, and a hotel name to run this report.'>
            <span style={{ display: 'inline-block', width: '355px' }}>
              <Button
                style={{ width: '100%' }}
                disabled
                data-testid='run-report'
              >
                Run Report
              </Button>
            </span>
          </Tooltip>
        </RunFooter>
      ) : (
        <RunFooter
          data-testid='gp-run-report-footer'
          style={{ position: 'sticky', bottom: 0 }}
        >
          <Button
            loading={loadingReport}
            style={{ width: '355px' }}
            className='ant-btn ant-btn-primary'
            onClick={askThenRun}
            disabled={loadingReport}
            data-testid='run-report'
          >
            {loadingReport
              ? 'Generating… This can take a couple of minutes'
              : 'Run Report'}
          </Button>
        </RunFooter>
      )}

      {downloadedFilename ? (
        <SuccessText data-testid='download-success'>
          {formatGpStockDownloadSuccess(downloadedFilename)}
        </SuccessText>
      ) : null}
    </StyledBlock>
  )
}

const StyledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const VenuePicker = styled.div`
  position: relative;
  width: 355px;
  margin-bottom: 15px;
  overflow: visible;
`

const HelperText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`

const FieldLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin: 4px 0 0;
`

const FieldHint = styled.p`
  font-size: 12px;
  color: #888;
  margin: 0 0 4px;
  max-width: 355px;
`

const StyledTextInput = styled.input`
  width: 355px;
  height: 40px;
  padding: 6px 11px;
  font-size: 16px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  box-sizing: border-box;
`

const PercentField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 355px;

  ${StyledTextInput} {
    flex: 1;
    width: auto;
    min-width: 0;
  }
`

const PercentSuffix = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #333;
`

const TargetsBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 355px;
`

const TargetRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const TargetVenueName = styled.span`
  font-size: 13px;
  color: #555;
`

const WarningText = styled.p`
  font-size: 13px;
  color: #8a6d3b;
  background: #fcf8e3;
  border: 1px solid #faebcc;
  border-radius: 4px;
  padding: 8px 10px;
  margin: 0 0 8px;
  max-width: 355px;
`

const SuccessText = styled.p`
  font-size: 13px;
  color: #3c763d;
  background: #dff0d8;
  border: 1px solid #d6e9c6;
  border-radius: 4px;
  padding: 8px 10px;
  margin: 8px 0 0;
  max-width: 355px;
  word-break: break-word;
`

const RunFooter = styled.div`
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: #fff;
  padding: 12px 0 4px;
  margin-top: 8px;
  border-top: 1px solid #f0f0f0;
`

const ModeRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 4px;
`

const ModeButton = styled.button`
  border: 1px solid ${props => (props.$active ? '#b5d922' : '#d9d9d9')};
  background: ${props => (props.$active ? '#f6ffed' : '#fff')};
  color: ${props => (props.$active ? '#333' : '#666')};
  border-radius: 4px;
  padding: 6px 12px;
  cursor: pointer;
  font-size: 13px;
`

const StyledRangePicker = styled(DatePicker.RangePicker)`
  .ant-calendar-picker-input.ant-input {
    height: 40px;
    padding: 6px 6px;
    font-size: 16px;
  }
`

const StyledMonthPicker = styled(DatePicker.MonthPicker)`
  .ant-calendar-picker-input.ant-input {
    height: 40px;
    padding: 6px 6px;
    font-size: 16px;
  }
`

export default GpStockPerformanceReport
